/**
 * Spotlight Constants (Phase 19E: UI Highlighting & Guided Tours)
 *
 * Centralized constants for spotlight overlay behavior.
 * Rule 15: No magic numbers — all delays, layout values, and z-index
 * values are defined here as the single source of truth.
 *
 * @module SpotlightConstants
 */

// ============================================================================
// Timing Delays (ms)
// ============================================================================

/** Spotlight timing delays — Rule 15: no magic numbers */
export const SPOTLIGHT_DELAYS = {
  /** Delay for element measurement after state update */
  ELEMENT_MEASUREMENT: 100,
  /** Delay for scroll animation to complete */
  SCROLL_COMPLETION: 400,
  /** Delay when starting spotlight on same page */
  SAME_PAGE_START: 500,
  /** Max wait time for waitForElement polling */
  ELEMENT_POLL_MAX_WAIT: 5000,
  /** Polling interval for waitForElement */
  ELEMENT_POLL_INTERVAL: 100,
  /** Wait for sidebar expand animation before measuring (sidebar CSS transition: 0.2s) */
  SIDEBAR_EXPAND_WAIT: 300,
} as const;

// ============================================================================
// Layout Constants (px)
// ============================================================================

/** Spotlight layout constants — Rule 15: no magic numbers */
export const SPOTLIGHT_LAYOUT = {
  /** Padding around the target element cutout */
  CUTOUT_PADDING: 8,
  /** Tooltip offset from the target element */
  TOOLTIP_OFFSET: 12,
  /** Minimum tooltip width */
  TOOLTIP_MIN_WIDTH: 260,
  /** Maximum tooltip width */
  TOOLTIP_MAX_WIDTH: 320,
  /** Left margin to keep tooltip from touching viewport edge */
  TOOLTIP_LEFT_MARGIN: 16,
  /** Right margin threshold to keep tooltip from touching right edge */
  TOOLTIP_RIGHT_MARGIN: 340,
  /** Fallback viewport width for SSR */
  VIEWPORT_FALLBACK_WIDTH: 600,
} as const;

// ============================================================================
// Z-Index Values
// ============================================================================

/** Spotlight z-index values — Rule 15: centralized z-index management */
export const SPOTLIGHT_Z_INDEX = {
  /** Backdrop z-index (below tooltip) */
  BACKDROP: 10001,
  /** Tooltip z-index (above backdrop) */
  TOOLTIP: 10002,
} as const;

// ============================================================================
// CSS Transition Durations
// ============================================================================

/** Transition durations for smooth step changes */
export const SPOTLIGHT_TRANSITIONS = {
  /** Duration for cutout/tooltip position transitions */
  STEP_CHANGE: '0.3s',
  /** Easing function */
  EASING: 'ease',
} as const;
